/**
 * "Log a call": a call, meeting or comment on the account, with its notes and, for calls and
 * meetings, a transcript from one of three places: recorded here, an uploaded recording or a
 * pasted transcript, or imported from a meeting tool through the notetaker.
 * A date in the future schedules it (upcoming, on the calendar); otherwise it's logged as done.
 */
import { Circle, Square } from "lucide-react";
import { useCreate, useNotify, type Identifier } from "ra-core";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import type { ActivityInput } from "../providers/zinspector/activities";
import {
  ACTIVITY_KINDS,
  type ActivityKind,
} from "../providers/zinspector/crmConfig";
import { toLocalInput } from "./activityUtils";
import { createNotetaker, type Transcript } from "./notetaker";
import { NotetakerImport } from "./NotetakerImport";
import { canRecordCalls, useCallRecorder } from "./useCallRecorder";

const NO_CONTACT = "__none";
const notetaker = createNotetaker();

interface FormState {
  kind: ActivityKind;
  title: string;
  when: string;
  contactId: string;
  notes: string;
  transcript: string;
  source: string;
}

const emptyForm = (): FormState => ({
  kind: "Call",
  title: "",
  when: toLocalInput(new Date()),
  contactId: NO_CONTACT,
  notes: "",
  transcript: "",
  source: "",
});

export const LogActivityForm = ({
  companyId,
  accountName,
  contacts,
  onLogged,
}: {
  companyId: Identifier;
  accountName: string;
  contacts: { id: Identifier; name: string }[];
  /** Called after a successful log, e.g. to close a dialog. */
  onLogged?: () => void;
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [transcribing, setTranscribing] = useState(false);
  const [create, { isPending }] = useCreate();
  const notify = useNotify();
  const recorder = useCallRecorder();
  const set = (patch: Partial<FormState>) =>
    setForm((f) => ({ ...f, ...patch }));
  const isComment = form.kind === "Comment";
  const canHaveTranscript = form.kind === "Call" || form.kind === "Meeting";
  const isUpcoming = !isComment && new Date(form.when).getTime() > Date.now();

  const applyTranscript = (result: Transcript, source: string) =>
    setForm((f) => ({
      ...f,
      notes: [f.notes.trim(), result.summary].filter(Boolean).join("\n\n"),
      transcript: result.transcript,
      source,
    }));

  const transcribe = async (audio: Blob, source: string) => {
    setTranscribing(true);
    try {
      applyTranscript(
        await notetaker.transcribe(audio, { title: form.title }),
        source,
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : "Transcription failed", {
        type: "error",
      });
    } finally {
      setTranscribing(false);
    }
  };

  // Transcribe as soon as a recording is ready.
  const { state: recorderState, recording, reset: resetRecorder } = recorder;
  useEffect(() => {
    if (recorderState !== "stopped" || !recording) return;
    resetRecorder();
    void transcribe(recording, "Recorded in zCRM");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorderState, recording]);

  const submit = () => {
    const data: ActivityInput = {
      company_id: companyId,
      kind: form.kind,
      title: form.title,
      starts_at: isComment ? undefined : new Date(form.when).toISOString(),
      contact_id: form.contactId === NO_CONTACT ? null : form.contactId,
      notes: form.notes,
      transcript: form.transcript,
      source: form.source,
      is_done: !isUpcoming,
    };
    if (isComment && !form.notes.trim()) {
      notify("Write the comment first", { type: "warning" });
      return;
    }
    create(
      "activities",
      { data },
      {
        onSuccess: () => {
          notify(
            isUpcoming ? `${form.kind} scheduled` : `${form.kind} logged`,
            {
              type: "success",
            },
          );
          setForm(emptyForm());
          onLogged?.();
        },
        onError: (error) =>
          notify(error instanceof Error ? error.message : "Logging failed", {
            type: "error",
          }),
      },
    );
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            value={form.kind}
            onValueChange={(kind) =>
              kind && set({ kind: kind as ActivityKind })
            }
            aria-label="What to log"
          >
            {ACTIVITY_KINDS.map((kind) => (
              <ToggleGroupItem key={kind} value={kind} className="px-4">
                {kind}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {notetaker.isMock && !isComment ? (
            <Badge variant="neutral">Notetaker: demo (mocked)</Badge>
          ) : null}
        </div>

        {isComment ? null : (
          <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-4">
            <Field label="What call is this" htmlFor="activity-title">
              <Input
                id="activity-title"
                placeholder={`Weekly sync with ${accountName}`}
                value={form.title}
                onChange={(e) => set({ title: e.target.value })}
              />
            </Field>
            <Field label="When" htmlFor="activity-when">
              <Input
                id="activity-when"
                type="datetime-local"
                value={form.when}
                onChange={(e) => set({ when: e.target.value })}
              />
            </Field>
            <Field label="With">
              <Select
                value={form.contactId}
                onValueChange={(contactId) => set({ contactId })}
              >
                <SelectTrigger aria-label="With" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTACT}>
                    No one in particular
                  </SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        {!canHaveTranscript || isUpcoming ? null : (
          <div className="flex flex-col gap-3">
            {canRecordCalls() ? (
              <div className="flex flex-wrap items-center gap-3">
                {recorder.state === "recording" ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={recorder.stop}
                  >
                    <Square className="fill-current" /> Stop and transcribe
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={recorder.start}
                  >
                    <Circle className="fill-destructive text-destructive" />{" "}
                    Record this call
                  </Button>
                )}
                <span className="text-sm text-muted-foreground">
                  Both sides: pick the call's tab or your screen and tick "Share
                  audio".
                </span>
              </div>
            ) : null}
            {recorder.error ? (
              <p className="text-sm text-destructive">{recorder.error}</p>
            ) : null}
            <details className="text-sm">
              <summary className="cursor-pointer">
                Or attach the recording, or paste the transcript
              </summary>
              <div className="flex flex-col gap-3 pt-3">
                <Input
                  type="file"
                  accept="audio/*,video/*"
                  aria-label="Recording file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void transcribe(file, "Uploaded recording");
                  }}
                />
                <Textarea
                  aria-label="Transcript"
                  placeholder="Paste the transcript"
                  rows={4}
                  value={form.transcript}
                  onChange={(e) =>
                    set({
                      transcript: e.target.value,
                      source: form.source || "Pasted transcript",
                    })
                  }
                />
              </div>
            </details>
            <NotetakerImport
              notetaker={notetaker}
              accountName={accountName}
              onImport={(recording) =>
                setForm((f) => ({
                  ...f,
                  title: recording.title,
                  when: toLocalInput(new Date(recording.started_at)),
                  notes: recording.summary,
                  transcript: recording.transcript,
                  source: recording.source,
                }))
              }
            />
            {transcribing ? (
              <p className="text-sm text-muted-foreground">Transcribing…</p>
            ) : null}
          </div>
        )}

        <Field label={isComment ? "Comment" : "Notes"} htmlFor="activity-notes">
          <Textarea
            id="activity-notes"
            rows={isComment ? 3 : 4}
            placeholder={
              isComment
                ? "What should the team know?"
                : "What was said, what was agreed, what's next"
            }
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={submit}
            disabled={
              isPending || transcribing || recorder.state === "recording"
            }
          >
            {isComment
              ? "Add comment"
              : isUpcoming
                ? `Schedule ${form.kind.toLowerCase()}`
                : `Log ${form.kind.toLowerCase()}`}
          </Button>
          <span className="text-sm text-muted-foreground">
            {isUpcoming
              ? "Goes on the calendar through zInspector's Google Calendar sync."
              : form.source
                ? `Transcript: ${form.source}.`
                : "Saved as a Follow-up task on the account in zInspector."}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const Field = ({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </div>
);
